import { useLocation, useParams } from 'react-router-dom';
import { CheckIcon } from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { getTicketQuery } from '@/api/queries';
import { Button } from '@packages/ui';
import { FirebaseClient } from '@/api/firebaseClient';
import { QRCode } from '@/components/QRCode';
import { Logo } from '@/components/Logo';
import header_transparent from '@/assets/wuestaffairs.png';

const adminUrl = import.meta.env.VITE_ADMIN_URL;

const client = new FirebaseClient();

export const TicketDetails = () => {
  const { search } = useLocation();

  const { ticketId } = useParams<{ ticketId: string }>();
  const { data: ticket } = useQuery(getTicketQuery(ticketId ?? ''));

  const qrCheckinUrl = adminUrl + '/checkin/' + ticket?.ticketId;

  const makePayment = async () => {
    if (!ticket) return;
    const getPaymentUrl = await client.getPaymentUrl(ticket?.ticketId);
    location.href = getPaymentUrl.data.url;
  };

  if (!ticket) return null;

  if (ticket.canceledAt) {
    return (
      <>
        <div className="bg-background/40 p-4 border border-destructive rounded">
          <h2 className="mt-2 font-bold text-warning">Ticket canceled</h2>
          <span className="text-destructive">
            Your ticket has been canceled and you cannot use it to enter the event.
          </span>
          <dl className="mt-4 [&>dd]:mb-2 [&>dt]:text-sm [&>dt]:text-foreground/80">
            <dt>Ticket number:</dt>
            <dd>{ticket.ticketId}</dd>
            <dt>Email:</dt>
            <dd>{ticket.email}</dd>
          </dl>
        </div>
      </>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-8 grid-cols-1">
      <div className="w-[400px] flex flex-col items-start">
        <img
          src={header_transparent}
          className="mb-8 max-w-full"
          style={{ width: 'min(70vw, 100%)' }}
        />
        {search.includes('success') ? (
          <>
            <h1 className="font-bold text-lg mb-8">Thank you for your payment.</h1>
          </>
        ) : null}
        {ticket.paidAt && !search.includes('success') ? (
          <div className="mb-8">
            <h1 className="text-center mb-2 font-bold">CHECKIN</h1>
            <div className="flex justify-center mb-10">
              <QRCode url={qrCheckinUrl} />
            </div>

            <dt>Payment:</dt>
            <dd>
              <div className="flex text-success items-center">
                <CheckIcon className="mr-2" />
                Payment received / processed on{' '}
                {Intl.DateTimeFormat('de-DE').format(new Date(ticket.paidAt))}
              </div>
            </dd>
          </div>
        ) : null}
        <h1 className="font-bold text-lg">Ticket Status</h1>
        {!ticket.paidAt ? (
          <div>
            <h2 className="mt-2 font-bold text-warning">Payment pending</h2>
            <span className="text-destructive">No payment received / processed</span>
            <div>
              <Button onClick={makePayment} className="mt-4">
                Pay now
              </Button>
            </div>
          </div>
        ) : null}
        <dl className="mt-4 [&>dd]:mb-2 [&>dt]:text-sm [&>dt]:text-foreground/80">
          <dt>Entry:</dt>
          <dd>08.11.2025 from 23:00 until 09.11.2025 08:00</dd>
          <dt>Ticket number:</dt>
          <dd>{ticket.ticketId}</dd>
          <dt>Name:</dt>
          <dd>{ticket.name}</dd>
          <dt>Email:</dt>
          <dd>{ticket.email}</dd>
          <dt>Ticket price:</dt>
          <dd>{ticket.price} EUR</dd>
        </dl>

        <>
          <h3 className="mt-4 text-lg font-medium">Location</h3>
          <p className="my-2">
            Club M01, Berlin <br />
            Markgrafendamm 1, 10245 Berlin <br />
            <a href="https://maps.app.goo.gl/UEKQZTEa5Ep1QFH98">Open in google maps</a>
          </p>
        </>
      </div>
      <div className="flex justify-center items-start">
        <Logo className="md:max-w-[340px]" />
      </div>
    </div>
  );
};
